import { useMemo } from 'react';

import { useFormState } from 'react-final-form';
import { Cloud } from '@cast/shared-types';
import { SelectedInstanceType } from '@cast/types';
import { EdgeConfiguration } from '@cast/types-omni';

import {
  NodeNameSection,
  InstanceConfigurationSection,
  EdgeConfigurationSection,
  ResourceOfferingSection,
  GpuConfigurationSection,
  GpuTimeSlicingSection,
  MultiInstanceGpuSection,
  KubernetesOrchestrationSection,
  DeleteProtectionSection,
  StorageSection,
} from './_sections';
import { getSelectedInstance } from './_utils';
import { getGpuTimeSlicingAvailability, getMigAvailability } from '../_utils';
import { ProvisionNodesCloudConfigurationFormValues } from '../types';
import { Stack } from '@cast/design-system';
import { EnhancedEdgeLocation } from '../../../types';

type Props = {
  availableInstances: SelectedInstanceType[];
  edgeConfigurations: EdgeConfiguration[];
  edgeLocation: EnhancedEdgeLocation;
};

export const ProvisionNodesCloudConfigurationSlideForm = ({
  edgeLocation,
  availableInstances,
  edgeConfigurations,
}: Props) => {
  const { values } = useFormState<ProvisionNodesCloudConfigurationFormValues>({
    subscription: { values: true },
  });

  const availableGpuTypes = useMemo(() => {
    if (
      !availableInstances ||
      !values.instanceType ||
      !values.availabilityZone
    ) {
      return [];
    }
    const selectedInstance = getSelectedInstance({
      availableInstances,
      instanceTypeName: values.instanceType,
      availabilityZone: values.availabilityZone,
    });

    const gpuTypes = (selectedInstance?.availableGpuDevices || []).map(
      (gpuDevice) => gpuDevice?.name ?? ''
    );

    return Array.from(new Set(gpuTypes));
  }, [availableInstances, values.instanceType, values.availabilityZone]);

  const gpuSettingsDisabled =
    edgeLocation?.provider !== Cloud.gcp || availableGpuTypes.length === 0;

  const timeSlicingAvailability = getGpuTimeSlicingAvailability({
    edgeLocation,
  });
  const migAvailability = getMigAvailability({
    edgeLocation,
  });

  const gpuTimeSlicingDisabled =
    !values.instanceType ||
    !values.availabilityZone ||
    availableGpuTypes.length === 0 ||
    !timeSlicingAvailability.available;

  const instanceTypeGatedHint = useMemo(() => {
    if (!values.instanceType) {
      return 'Select instance type first.';
    }

    return undefined;
  }, [values.instanceType]);

  const gpuTypeHint = useMemo(() => {
    if (instanceTypeGatedHint) {
      return instanceTypeGatedHint;
    }

    if (!values.availabilityZone) {
      return 'Select availability zone first.';
    }

    if (availableGpuTypes.length === 0) {
      return "The selected instance type doesn't support GPU configuration.";
    }

    return undefined;
  }, [
    instanceTypeGatedHint,
    values.availabilityZone,
    availableGpuTypes.length,
  ]);

  const gpuCountHint = useMemo(() => {
    if (gpuTypeHint) {
      return gpuTypeHint;
    }

    if (!values.gpuType) {
      return 'Select GPU type first.';
    }

    return undefined;
  }, [gpuTypeHint, values.gpuType]);

  const gpuTimeSlicingHint = useMemo(() => {
    if (!timeSlicingAvailability.available) {
      return timeSlicingAvailability.reason;
    }

    if (instanceTypeGatedHint) {
      return instanceTypeGatedHint;
    }

    if (!values.availabilityZone) {
      return 'Select availability zone first.';
    }

    if (availableGpuTypes.length === 0) {
      return "The selected instance type doesn't support GPU configuration.";
    }

    return undefined;
  }, [
    timeSlicingAvailability,
    instanceTypeGatedHint,
    values.availabilityZone,
    availableGpuTypes.length,
  ]);

  return (
    <Stack>
      <NodeNameSection />

      <InstanceConfigurationSection
        availableInstances={availableInstances}
        availabilityZoneHint={instanceTypeGatedHint}
      />

      <EdgeConfigurationSection edgeConfigurations={edgeConfigurations} />

      <ResourceOfferingSection />

      {edgeLocation?.provider === Cloud.gcp && (
        <GpuConfigurationSection
          availableInstances={availableInstances}
          availableGpuTypes={availableGpuTypes}
          gpuSettingsDisabled={gpuSettingsDisabled}
          gpuTypeHint={gpuTypeHint}
          gpuCountHint={gpuCountHint}
        />
      )}

      <GpuTimeSlicingSection
        disabled={gpuTimeSlicingDisabled}
        hint={gpuTimeSlicingHint}
      />

      <MultiInstanceGpuSection
        disabled={!migAvailability.available}
        hint={!migAvailability.available ? migAvailability.reason : undefined}
      />
      <KubernetesOrchestrationSection />

      <DeleteProtectionSection />

      <StorageSection />
    </Stack>
  );
};
